import React from 'react';
import { Type, Hash, CheckSquare, SquareStack, Box, CircleDot } from 'lucide-react';
import { PlaybookTransformItem, PlaybookTransformType, PlaybookMapping, PlaybookEnrichment, PlaybookExpression, PlaybookSpreading, PlaybookFilter, PlaybookTransformContent, PlaybookEventConfig } from '../../types/playbook';
import { EventSchema } from '@/types/schema';
import { EditorSelection } from './PlaybookEditorTab';

// Helper to truncate paths for display
export const truncatePath = (path: string, maxLength: number = 50): string => {
  if (path.length <= maxLength) {
    return path;
  }
  const parts = path.split('.');
  if (parts.length === 1) {
    return path.substring(0, maxLength - 3) + '...';
  }
  const lastPart = parts[parts.length - 1];
  const firstPart = parts[0];
  const ellipsis = '...';
  const availableLength = maxLength - ellipsis.length - lastPart.length;

  if (availableLength <= firstPart.length + 3) {
    return firstPart.substring(0, availableLength - 3) + ellipsis + lastPart;
  }

  return firstPart + ellipsis + lastPart;
};

// Get the appropriate icon for the data type
export const getTypeIcon = (dataType?: string): JSX.Element => {
  switch (dataType) {
    case 'string':
      return <Type size={16} />;
    case 'number':
      return <Hash size={16} />;
    case 'boolean':
      return <CheckSquare size={16} />;
    case 'array':
      return <SquareStack size={16} />;
    case 'object':
      return <Box size={16} />;
    case 'null':
      return <CircleDot size={16} />;
    default:
      return <Type size={16} />;
  }
};

// Get display name for transform type
export const getTransformDisplayName = (type: PlaybookTransformType): string => {
  switch (type) {
    case 'enrichment': return 'Enrichment';
    case 'mapping': return 'Mapping';
    case 'expression': return 'Expression';
    case 'spreading': return 'Spreading';
    case 'filter': return 'Filter';
    default: return 'Transform';
  }
};

// Get transform detail display
export const getTransformDetailDisplay = (transform: PlaybookTransformItem): string => {
  if (!transform.content) return '';

  switch (transform.type) {
    case 'mapping':
      const mappingContent = transform.content as PlaybookMapping;
      if (mappingContent.transforms && mappingContent.transforms.length > 0) {
        return `${mappingContent.transforms.map((t: { type: string }) => t.type).join(', ')}`;
      }
      return '';
    case 'enrichment':
      const enrichmentContent = transform.content as PlaybookEnrichment;
      if (enrichmentContent.type) {
        return `${enrichmentContent.type}`;
      }
      return '';
    case 'expression':
      const expressionContent = transform.content as PlaybookExpression;
      const outputCount = expressionContent.declaredOutputProperties?.length || 0;
      if (outputCount > 1) {
        return `${outputCount} outputs`;
      }
      return '';
    case 'spreading':
      const spreadingContent = transform.content as PlaybookSpreading;
      if (spreadingContent.prefix) {
        return `Prefix: ${spreadingContent.prefix}`;
      }
      return '';
    case 'filter':
      // For filters, we'll show the name if available
      if (transform.name) {
        return `${transform.name}`;
      }
      return '';
    default:
      return '';
  }
};

// Get default content for a transform type
export const getDefaultContent = (type: PlaybookTransformType): PlaybookTransformContent => {
  switch (type) {
    case 'enrichment':
      return {
        type: 'DirectValue',
        outputKey: '',
        value: '',
      } as PlaybookEnrichment;
    case 'mapping':
      return {
        inputKey: '',
        outputKey: '',
        transforms: [],
      } as PlaybookMapping;
    case 'expression':
      return {
        lang: 'lua',
        body: '',
        declaredOutputProperties: [],
      } as PlaybookExpression;
    case 'spreading':
      return {
        inputKey: '',
        prefix: '',
        separator: '.',
        depth: 1,
        arrayStartIndex: 0,
        inferredOutputProperties: [],
      } as PlaybookSpreading;
    case 'filter':
      return {
        rules: '',
      } as PlaybookFilter;
    default:
      return {
        type: 'DirectValue',
        outputKey: '',
        value: '',
      } as PlaybookEnrichment;
  }
};

// Get mapping type display
export const getMappingTypeDisplay = (transform: PlaybookTransformItem): JSX.Element | null => {
  let mappingType = transform.mappingType;
  let text = '';
  let bgColor = 'bg-gray-100 dark:bg-gray-700';
  let textColor = 'text-gray-800 dark:text-gray-200';

  // Prioritize YAML tag if it was imported from YAML
  if (transform.__yamlMetadata) {
    mappingType = 'yaml-imported';
  }

  if (!mappingType) return null;

  switch (mappingType) {
    case 'direct':
      text = 'Auto-match';
      bgColor = 'bg-blue-100 dark:bg-blue-900';
      textColor = 'text-blue-800 dark:text-blue-200';
      break;
    case 'ai':
      const certaintyScore = transform.__meta?.certaintyScore;
      text = certaintyScore !== undefined ? `AI (${Math.round(certaintyScore * 100)}%)` : 'AI';
      bgColor = 'bg-green-100 dark:bg-green-900';
      textColor = 'text-green-800 dark:text-green-200';
      break;
    case 'manual':
      text = 'Manual';
      bgColor = 'bg-gray-200 dark:bg-gray-600';
      textColor = 'text-gray-800 dark:text-gray-100';
      break;
    case 'no-match':
      text = 'No Match';
      bgColor = 'bg-red-100 dark:bg-red-900';
      textColor = 'text-red-800 dark:text-red-200';
      break;
    case 'regex':
      text = 'Auto-map';
      bgColor = 'bg-purple-100 dark:bg-purple-900';
      textColor = 'text-purple-800 dark:text-purple-200';
      break;
    case 'yaml-imported':
      text = 'YAML';
      bgColor = 'bg-yellow-100 dark:bg-yellow-900';
      textColor = 'text-yellow-800 dark:text-yellow-200';
      break;
    default:
      return null;
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${bgColor} ${textColor} whitespace-nowrap`}>
      {text}
    </span>
  );
};

// Get section title for the panel
export const getSectionTitle = (selection: EditorSelection, selectedEvent: PlaybookEventConfig | null): string => {
  if (selection.type === 'global') {
    return 'Global Transforms';
  } else if (selection.type === 'event' && selectedEvent) {
    return `${selectedEvent.name} Transforms`;
  }
  return 'Transforms';
};

// Helper to determine if a property pair is applicable to ALL events
export const isPropertyPairApplicableToAllEvents = (inputPath: string | undefined, outputPath: string | undefined, allEvents: EventSchema[]): boolean => {
  if (!allEvents || allEvents.length === 0) {
    return false; // If no events, it can't apply to ALL events
  }

  return allEvents.every(event => {
    const eventPaths = new Set(event.parameterFullPaths);
    const inputMatches = !inputPath || eventPaths.has(inputPath);
    const outputMatches = !outputPath || eventPaths.has(outputPath);
    return inputMatches && outputMatches;
  });
};

// Sorting functionality
export const handleSort = (
  column: 'inputKey' | 'transformType' | 'outputKey' | 'mappingType' | 'events',
  sortBy: string,
  sortDirection: 'asc' | 'desc',
  setSortBy: React.Dispatch<React.SetStateAction<any>>, // Changed type to any
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>> // Changed type to any
) => {
  if (sortBy === column) {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setSortBy(column);
    setSortDirection('asc');
  }
};
